import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type InfiniteData } from '@tanstack/react-query'
import { BsFolderFill } from 'react-icons/bs'
import { HiOutlineGlobeAlt, HiOutlineStar, HiStar } from 'react-icons/hi2'
import { LuChevronRight, LuLink2 } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import PopoverV2 from '@/components/common/PopoverV2'
import { Skeleton } from '@/components/common/Skeleton'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/common/Tabs'
import { Input } from '@/components/common/TextInputV2'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import {
  type SharepointSavedLocation,
  createSiteLocation,
  useSharepointPinnedLocations,
  useSharepointRecentLocations,
  useSharepointRecordDriveVisit,
  useSharepointRecordSiteVisit,
  useSharepointTogglePinnedLocation,
} from '@/components/integrations/SharepointFilePicker/location-store'
import {
  EMPTY_DRIVES,
  EMPTY_SITES,
} from '@/components/integrations/SharepointFilePicker/model'

import { ApiError } from '@/context/usaspending/liveFetcher'

import {
  type SharePointDrive,
  type SharePointDriveListResponse,
  type SharePointSite,
} from '@/hooks/sharepoint/types'
import { useResolveSharepointLink } from '@/hooks/sharepoint/useResolveSharepointLink'
import { useSharepointDrives } from '@/hooks/sharepoint/useSharepointDrives'
import {
  type FetchSharepointSitesResponse,
  useInfiniteSharepointSites,
} from '@/hooks/sharepoint/useSharepointSites'
import { useInfiniteSharepointSubsites } from '@/hooks/sharepoint/useSharepointSubsites'

import type { UUID } from '@/types/Id'

const SORT_OPTIONS = {
  numeric: true,
  sensitivity: 'base',
} as const

const LOAD_MORE_TEXT_PADDING_CLASS = 'pl-3.5'
const MAX_VISIBLE_BROWSE_BREADCRUMBS = 3
const EXPLORER_TABS = [
  { compactLabel: 'Browse', label: 'Browse', value: 'browse' },
  { compactLabel: 'Recent', label: 'Recent', value: 'recent' },
  { compactLabel: 'Pinned', label: 'Pinned', value: 'pinned' },
  { compactLabel: 'Open', label: 'Open Link', value: 'open-link' },
] as const

const preventPopoverOpenAutoFocus = (event: Event) => {
  event.preventDefault()
}

type ExplorerTab = (typeof EXPLORER_TABS)[number]['value']
type ExplorerRowKind = 'drive' | 'site'

interface SharepointSiteDriveTreeProps {
  className?: string
  defaultBrowsePath?: SharePointSite[]
  defaultBrowseSite?: SharePointSite | null
  emptyText?: string
  onClose?: () => void
  onCommitBrowsePath?: (sitePath: SharePointSite[]) => void
  onNavigateToSite?: (site: SharePointSite | null) => void
  onSelectDrive: (site: SharePointSite, drive: SharePointDrive) => void
  orgId: UUID | ''
  scrollClassName?: string
  selectedDriveId?: string
  selectedSiteId?: string
}

interface ExplorerRowProps {
  actionLabel?: string
  hideLeadingIcon?: boolean
  isActionActive?: boolean
  isActive?: boolean
  kind: ExplorerRowKind
  onActionClick?: () => void
  onClick: () => void
  sublabel?: string | null
  title: string
}

interface BrowseBreadcrumbItem {
  actualIndex: number
  id: string
  label: string
}

interface HiddenBreadcrumbButtonProps {
  breadcrumb: BrowseBreadcrumbItem
  onSelectIndex: (index: number) => void
}

interface SiteExplorerRowProps {
  onOpenSite: (site: SharePointSite) => void
  onTogglePinnedSite: (site: SharePointSite) => void
  pinnedLocationKeys: ReadonlySet<string>
  selectedDriveId?: string
  selectedSiteId?: string
  site: SharePointSite
}

interface DriveExplorerRowProps {
  drive: SharePointDrive
  onSelectDrive: (site: SharePointSite, drive: SharePointDrive) => void
  selectedDriveId?: string
  site: SharePointSite
}

interface SavedLocationRowProps {
  location: SharepointSavedLocation
  onSelectLocation: (location: SharepointSavedLocation) => void
  onTogglePinnedLocation?: (location: SharepointSavedLocation) => void
  selectedDriveId?: string
  selectedSiteId?: string
}

interface BrowsePanelProps {
  browseSites: SharePointSite[]
  currentBrowseSite: SharePointSite | null
  driveBrowseSite: SharePointSite | null
  drives: SharePointDrive[]
  emptyText: string
  hasMoreSites: boolean
  isBrowseLoading: boolean
  isFetchingMoreSites: boolean
  onLoadMoreSites: () => void
  onOpenSite: (site: SharePointSite) => void
  onSelectDrive: (site: SharePointSite, drive: SharePointDrive) => void
  onTogglePinnedSite: (site: SharePointSite) => void
  pinnedLocationKeys: ReadonlySet<string>
  selectedDriveId?: string
  selectedSiteId?: string
}

interface SavedLocationsPanelProps {
  emptyText: string
  locations: SharepointSavedLocation[]
  onSelectLocation: (location: SharepointSavedLocation) => void
  onTogglePinnedLocation?: (location: SharepointSavedLocation) => void
  selectedDriveId?: string
  selectedSiteId?: string
}

interface OpenLinkPanelProps {
  isResolvingLink: boolean
  linkError: string | null
  linkValue: string
  onLinkValueChange: (value: string) => void
  onResolveLink: () => void
}

const compareDriveLabels = (
  left: SharePointDrive,
  right: SharePointDrive,
): number => {
  return left.name.localeCompare(right.name, undefined, SORT_OPTIONS)
}

const getDefaultBrowsePath = ({
  defaultBrowsePath,
  defaultBrowseSite,
}: {
  defaultBrowsePath?: SharePointSite[]
  defaultBrowseSite?: SharePointSite | null
}) => {
  if (defaultBrowsePath && defaultBrowsePath.length > 0) {
    return [...defaultBrowsePath]
  }

  return defaultBrowseSite ? [defaultBrowseSite] : []
}

const getSharepointSiteIdentity = (site: SharePointSite) => {
  return site.id || site.webUrl
}

const getSharepointSitePathKey = (sites: SharePointSite[]) => {
  return sites.map(getSharepointSiteIdentity).join('|')
}

const dedupeSharepointSites = (sites: SharePointSite[]) => {
  const dedupedSites: SharePointSite[] = []
  const seenSiteIds = new Set<string>()

  for (const site of sites) {
    const siteId = getSharepointSiteIdentity(site)
    if (seenSiteIds.has(siteId)) {
      continue
    }
    seenSiteIds.add(siteId)
    dedupedSites.push(site)
  }

  return dedupedSites
}

const selectFlattenedSites = (
  data: InfiniteData<FetchSharepointSitesResponse>,
): SharePointSite[] => {
  return dedupeSharepointSites(data.pages.flatMap((page) => page.data))
}

const selectSortedDrives = (
  data: SharePointDriveListResponse,
): SharePointDrive[] => {
  return [...data.data].toSorted(compareDriveLabels)
}

const normalizeSharepointUrl = (value: string) => {
  try {
    const url = new URL(value)
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`.toLowerCase()
  } catch {
    return ''
  }
}

const findLongestUrlMatch = <T,>(
  items: readonly T[],
  normalizedTarget: string,
  getUrl: (item: T) => string,
) => {
  let matchedItem: T | null = null
  let matchedLength = -1

  for (const item of items) {
    const normalizedUrl = normalizeSharepointUrl(getUrl(item))
    if (
      normalizedUrl &&
      normalizedTarget.startsWith(normalizedUrl) &&
      normalizedUrl.length > matchedLength
    ) {
      matchedItem = item
      matchedLength = normalizedUrl.length
    }
  }

  return matchedItem
}

const isDescendantSharepointUrl = (
  ancestorUrl: string,
  candidateUrl: string,
) => {
  const normalizedAncestor = normalizeSharepointUrl(ancestorUrl)
  const normalizedCandidate = normalizeSharepointUrl(candidateUrl)

  if (
    !normalizedAncestor ||
    !normalizedCandidate ||
    normalizedAncestor === normalizedCandidate
  ) {
    return false
  }

  return normalizedCandidate.startsWith(`${normalizedAncestor}/`)
}

const resolveBrowsePath = ({
  rootSites,
  sitePath,
}: {
  rootSites: SharePointSite[]
  sitePath: SharePointSite[]
}) => {
  if (sitePath.length !== 1) {
    return sitePath
  }

  const [leafSite] = sitePath
  const normalizedLeafUrl = normalizeSharepointUrl(leafSite.webUrl)
  if (!normalizedLeafUrl) {
    return sitePath
  }

  const ancestorSite = findLongestUrlMatch(
    rootSites.filter(
      (site) =>
        site.id !== leafSite.id &&
        isDescendantSharepointUrl(site.webUrl, leafSite.webUrl),
    ),
    normalizedLeafUrl,
    (site) => site.webUrl,
  )

  return ancestorSite ? [ancestorSite, leafSite] : sitePath
}

const getResolveLinkErrorMessage = (
  error: unknown,
  fallback = 'Could not resolve that link right now.',
) => {
  if (error instanceof ApiError) {
    if (error.status >= 500) {
      return fallback
    }

    return error.detail || fallback
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

const createSiteFromSavedLocation = (
  location: SharepointSavedLocation,
): SharePointSite => ({
  createdDateTime: '',
  id: location.siteId,
  name: location.siteName,
  webUrl: location.siteWebUrl,
})

const createDriveFromSavedLocation = (
  location: SharepointSavedLocation,
): SharePointDrive | null => {
  if (location.type !== 'drive' || !location.driveId || !location.driveName) {
    return null
  }

  return {
    drive_type: '',
    id: location.driveId,
    name: location.driveName,
    web_url: location.driveWebUrl ?? '',
  }
}

const ExplorerRow = memo(function ExplorerRow({
  actionLabel,
  hideLeadingIcon = false,
  isActionActive = false,
  isActive = false,
  kind,
  onActionClick,
  onClick,
  sublabel,
  title,
}: ExplorerRowProps) {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onClick()
      }
    },
    [onClick],
  )

  const handleActionClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      onActionClick?.()
    },
    [onActionClick],
  )

  const handleActionKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onActionClick?.()
      }
    },
    [onActionClick],
  )

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        'group flex items-center gap-1.5 px-3.5 outline-none',
        hideLeadingIcon && !sublabel ? 'py-[7px]' : 'py-2',
        isActive
          ? 'bg-primary-100/80 dark:bg-primary-800/30'
          : 'hover:bg-primary-100 dark:hover:bg-primary-800/40',
        'cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-300 dark:focus-visible:ring-primary-700',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
        {!hideLeadingIcon ? (
          <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary-500 dark:text-primary-400">
            {kind === 'drive' ? (
              <BsFolderFill size={14} className="block" />
            ) : kind === 'site' ? (
              <HiOutlineGlobeAlt size={16} className="block" />
            ) : null}
          </span>
        ) : null}
        <span className="min-w-0 flex-1">
          <span
            className={twMerge(
              'block truncate text-sm',
              'leading-5',
              isActive
                ? 'font-medium text-primary-900 dark:text-primary-100'
                : 'text-primary-700 dark:text-primary-300',
            )}
          >
            {title}
          </span>
          {sublabel ? (
            <span className="mt-0.5 block truncate text-xs leading-4 text-primary-500 dark:text-primary-400">
              {sublabel}
            </span>
          ) : null}
        </span>
      </div>

      {onActionClick ? (
        <button
          type="button"
          onClick={handleActionClick}
          onKeyDown={handleActionKeyDown}
          aria-label={actionLabel}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] text-primary-400 hover:bg-primary-200 hover:text-primary-700 dark:text-primary-500 dark:hover:bg-primary-800 dark:hover:text-primary-200"
        >
          {isActionActive ? (
            <HiStar size={15} className="text-amber-500 dark:text-amber-400" />
          ) : (
            <HiOutlineStar size={15} />
          )}
        </button>
      ) : null}
    </div>
  )
})
ExplorerRow.displayName = 'ExplorerRow'

interface SectionLabelProps {
  isExpanded: boolean
  onToggle: () => void
  showTopBorder?: boolean
  children: React.ReactNode
}

const SectionLabel = memo(function SectionLabel({
  isExpanded,
  onToggle,
  showTopBorder = false,
  children,
}: SectionLabelProps) {
  const handleClick = useCallback(() => {
    onToggle()
  }, [onToggle])

  return (
    <button
      type="button"
      onClick={handleClick}
      className={twMerge(
        'sticky top-0 z-10 flex h-8 w-full items-center gap-2 border-b border-primary-200 bg-white pl-3 pr-3.5 text-left text-[13px] font-medium text-primary-500 dark:border-primary-800 dark:bg-primary-900 dark:text-primary-400',
        showTopBorder ? 'border-t' : 'border-t-0',
      )}
    >
      <LuChevronRight
        size={12}
        className={twMerge(
          'shrink-0 transition-transform',
          isExpanded ? 'rotate-90' : 'rotate-0',
        )}
      />
      <span>{children}</span>
    </button>
  )
})
SectionLabel.displayName = 'SectionLabel'

interface LoadMoreButtonProps {
  isLoading: boolean
  onClick: () => void
}

const LoadMoreButton = memo(function LoadMoreButton({
  isLoading,
  onClick,
}: LoadMoreButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      className={twMerge(
        'block w-full pb-3.5 pt-1.5 pr-3.5 text-left text-xs font-medium text-blue-600 transition-colors hover:text-blue-700 hover:underline disabled:cursor-wait disabled:text-blue-400 dark:text-blue-400 dark:hover:text-blue-300 dark:disabled:text-blue-500',
        LOAD_MORE_TEXT_PADDING_CLASS,
      )}
    >
      {isLoading ? 'Loading more sites…' : 'Load more sites'}
    </button>
  )
})
LoadMoreButton.displayName = 'LoadMoreButton'

const HiddenBreadcrumbButton = memo(function HiddenBreadcrumbButton({
  breadcrumb,
  onSelectIndex,
}: HiddenBreadcrumbButtonProps) {
  const handleClick = useCallback(() => {
    onSelectIndex(breadcrumb.actualIndex)
  }, [breadcrumb.actualIndex, onSelectIndex])

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] text-primary-700 transition-colors hover:bg-primary-100 dark:text-primary-200 dark:hover:bg-primary-800"
    >
      <LuChevronRight
        size={11}
        className="shrink-0 text-primary-400 dark:text-primary-500"
      />
      <span className="truncate">{breadcrumb.label}</span>
    </button>
  )
})
HiddenBreadcrumbButton.displayName = 'HiddenBreadcrumbButton'

interface BrowseBreadcrumbsProps {
  isCurrentSitePinned?: boolean
  onTogglePinnedCurrentSite?: () => void
  onSelectIndex: (index: number) => void
  path: SharePointSite[]
}

interface BrowseBreadcrumbButtonProps {
  ariaCurrent?: 'page'
  className: string
  label: string
  onSelectIndex?: (index: number) => void
  selectIndex?: number
}

const BrowseBreadcrumbButton = memo(function BrowseBreadcrumbButton({
  ariaCurrent,
  className,
  label,
  onSelectIndex,
  selectIndex,
}: BrowseBreadcrumbButtonProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const handlePointerEnter = useCallback(() => {
    setIsTooltipOpen(true)
  }, [])

  const handlePointerLeave = useCallback(() => {
    setIsTooltipOpen(false)
  }, [])

  const handleFocus = useCallback(() => {
    setIsTooltipOpen(false)
  }, [])

  const handleBlur = useCallback(() => {
    setIsTooltipOpen(false)
  }, [])

  const handleClick = useCallback(() => {
    setIsTooltipOpen(false)
    if (selectIndex != null) {
      onSelectIndex?.(selectIndex)
    }
  }, [onSelectIndex, selectIndex])

  return (
    <Tooltip open={isTooltipOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-current={ariaCurrent}
          onClick={selectIndex != null ? handleClick : undefined}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={className}
        >
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" inverseColors>
        {label}
      </TooltipContent>
    </Tooltip>
  )
})
BrowseBreadcrumbButton.displayName = 'BrowseBreadcrumbButton'

const BrowseBreadcrumbs = memo(function BrowseBreadcrumbs({
  isCurrentSitePinned = false,
  onTogglePinnedCurrentSite,
  path,
  onSelectIndex,
}: BrowseBreadcrumbsProps) {
  const [isOverflowPopoverOpen, setIsOverflowPopoverOpen] = useState(false)

  const breadcrumbs = useMemo(
    () => [
      { actualIndex: -1, id: 'all-sites', label: 'All sites' },
      ...path.map((site, index) => ({
        actualIndex: index,
        id: site.id,
        label: site.name || 'Unnamed site',
      })),
    ],
    [path],
  )

  const displayItems = useMemo(() => {
    if (breadcrumbs.length <= MAX_VISIBLE_BROWSE_BREADCRUMBS) {
      return breadcrumbs.map((breadcrumb) => ({
        type: 'breadcrumb' as const,
        breadcrumb,
      }))
    }

    const tailCount = MAX_VISIBLE_BROWSE_BREADCRUMBS - 1
    const tailStartIndex = breadcrumbs.length - tailCount

    return [
      {
        type: 'breadcrumb' as const,
        breadcrumb: breadcrumbs[0],
      },
      {
        type: 'ellipsis' as const,
        hiddenItems: breadcrumbs.slice(1, tailStartIndex),
      },
      ...breadcrumbs.slice(tailStartIndex).map((breadcrumb) => ({
        type: 'breadcrumb' as const,
        breadcrumb,
      })),
    ]
  }, [breadcrumbs])

  return (
    <div className="flex h-11 items-center gap-2 border-b border-primary-200 pl-2 pr-3.5 dark:border-primary-800">
      <div className="flex min-w-0 flex-1 items-center py-1">
        <nav className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
          {displayItems.map((item, index) => {
            if (item.type === 'ellipsis') {
              return (
                <React.Fragment key="ellipsis">
                  <LuChevronRight
                    size={12}
                    className="shrink-0 text-primary-400 dark:text-primary-500"
                  />
                  <PopoverV2
                    open={isOverflowPopoverOpen}
                    onOpenChange={setIsOverflowPopoverOpen}
                  >
                    <PopoverV2.Trigger asChild>
                      <button
                        type="button"
                        aria-label="Show hidden breadcrumbs"
                        className="shrink-0 rounded-md px-1.5 py-0.5 text-[13px] leading-5 text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-800 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-200"
                      >
                        …
                      </button>
                    </PopoverV2.Trigger>
                    <PopoverV2.Content
                      side="bottom"
                      align="start"
                      onOpenAutoFocus={preventPopoverOpenAutoFocus}
                      className="w-auto min-w-[180px] max-w-[260px] p-1"
                    >
                      {item.hiddenItems.map((breadcrumb) => (
                        <HiddenBreadcrumbButton
                          key={breadcrumb.id}
                          breadcrumb={breadcrumb}
                          onSelectIndex={onSelectIndex}
                        />
                      ))}
                    </PopoverV2.Content>
                  </PopoverV2>
                </React.Fragment>
              )
            }

            const isLast = index === displayItems.length - 1
            return (
              <React.Fragment key={item.breadcrumb.id}>
                {index > 0 ? (
                  <LuChevronRight
                    size={12}
                    className="shrink-0 text-primary-400 dark:text-primary-500"
                  />
                ) : null}
                <BrowseBreadcrumbButton
                  ariaCurrent={isLast ? 'page' : undefined}
                  label={item.breadcrumb.label}
                  onSelectIndex={onSelectIndex}
                  selectIndex={isLast ? undefined : item.breadcrumb.actualIndex}
                  className={twMerge(
                    'min-w-0 rounded-md px-1.5 py-0.5 text-[13px] leading-5 transition-colors',
                    isLast
                      ? 'cursor-default truncate font-medium text-primary-900 dark:text-primary-100'
                      : item.breadcrumb.actualIndex === -1
                        ? 'shrink-0 text-primary-500 hover:bg-primary-100 hover:text-primary-800 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-200'
                        : 'truncate text-primary-500 hover:bg-primary-100 hover:text-primary-800 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-200',
                  )}
                />
              </React.Fragment>
            )
          })}
        </nav>
      </div>

      {onTogglePinnedCurrentSite ? (
        <button
          type="button"
          onClick={onTogglePinnedCurrentSite}
          aria-label={isCurrentSitePinned ? 'Unpin site' : 'Pin site'}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] text-primary-400 hover:bg-primary-200 hover:text-primary-700 dark:text-primary-500 dark:hover:bg-primary-800 dark:hover:text-primary-200"
        >
          {isCurrentSitePinned ? (
            <HiStar size={15} className="text-amber-500 dark:text-amber-400" />
          ) : (
            <HiOutlineStar size={15} />
          )}
        </button>
      ) : (
        <span className="h-7 w-7 shrink-0" aria-hidden="true" />
      )}
    </div>
  )
})
BrowseBreadcrumbs.displayName = 'BrowseBreadcrumbs'

const SiteExplorerRow = memo(function SiteExplorerRow({
  onOpenSite,
  onTogglePinnedSite,
  pinnedLocationKeys,
  selectedDriveId,
  selectedSiteId,
  site,
}: SiteExplorerRowProps) {
  const locationKey = createSiteLocation(site).key
  const isPinned = pinnedLocationKeys.has(locationKey)

  const handleClick = useCallback(() => {
    onOpenSite(site)
  }, [onOpenSite, site])

  const handleActionClick = useCallback(() => {
    onTogglePinnedSite(site)
  }, [onTogglePinnedSite, site])

  return (
    <ExplorerRow
      kind="site"
      hideLeadingIcon
      title={site.name || 'Unnamed site'}
      isActive={selectedSiteId === site.id && !selectedDriveId}
      onClick={handleClick}
      onActionClick={handleActionClick}
      actionLabel={isPinned ? 'Unpin location' : 'Pin location'}
      isActionActive={isPinned}
    />
  )
})
SiteExplorerRow.displayName = 'SiteExplorerRow'

const DriveExplorerRow = memo(function DriveExplorerRow({
  drive,
  onSelectDrive,
  selectedDriveId,
  site,
}: DriveExplorerRowProps) {
  const handleClick = useCallback(() => {
    onSelectDrive(site, drive)
  }, [drive, onSelectDrive, site])

  return (
    <ExplorerRow
      kind="drive"
      title={drive.name}
      isActive={selectedDriveId === drive.id}
      onClick={handleClick}
    />
  )
})
DriveExplorerRow.displayName = 'DriveExplorerRow'

const SavedLocationRow = memo(function SavedLocationRow({
  location,
  onSelectLocation,
  onTogglePinnedLocation,
  selectedDriveId,
  selectedSiteId,
}: SavedLocationRowProps) {
  const handleClick = useCallback(() => {
    onSelectLocation(location)
  }, [location, onSelectLocation])

  const handleActionClick = useCallback(() => {
    if (location.type === 'site') {
      onTogglePinnedLocation?.(location)
    }
  }, [location, onTogglePinnedLocation])

  return (
    <ExplorerRow
      kind={location.type}
      title={location.driveName ?? location.siteName}
      sublabel={
        location.type === 'drive' ? location.siteName : location.siteWebUrl
      }
      isActive={
        location.type === 'drive'
          ? selectedDriveId === location.driveId
          : selectedSiteId === location.siteId && !selectedDriveId
      }
      onClick={handleClick}
      onActionClick={
        location.type === 'site' && onTogglePinnedLocation
          ? handleActionClick
          : undefined
      }
      actionLabel={
        location.type === 'site'
          ? location.pinned
            ? 'Unpin site'
            : 'Pin site'
          : undefined
      }
      isActionActive={location.type === 'site' && location.pinned}
    />
  )
})
SavedLocationRow.displayName = 'SavedLocationRow'

const BrowsePanel = memo(function BrowsePanel({
  browseSites,
  currentBrowseSite,
  driveBrowseSite,
  drives,
  emptyText,
  hasMoreSites,
  isBrowseLoading,
  isFetchingMoreSites,
  onLoadMoreSites,
  onOpenSite,
  onSelectDrive,
  onTogglePinnedSite,
  pinnedLocationKeys,
  selectedDriveId,
  selectedSiteId,
}: BrowsePanelProps) {
  const [isSubsitesExpanded, setIsSubsitesExpanded] = useState(true)
  const [isLibrariesExpanded, setIsLibrariesExpanded] = useState(true)

  const handleToggleSubsites = useCallback(() => {
    setIsSubsitesExpanded((current) => !current)
  }, [])

  const handleToggleLibraries = useCallback(() => {
    setIsLibrariesExpanded((current) => !current)
  }, [])

  const isRootBrowseView = currentBrowseSite == null
  const hasSubsitesSection = currentBrowseSite != null && browseSites.length > 0
  const hasLibrariesSection = driveBrowseSite != null && drives.length > 0

  if (isBrowseLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-10 rounded-md" />
        ))}
      </div>
    )
  }

  if (browseSites.length === 0 && drives.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-[12px] text-primary-400 dark:text-primary-500">
        {currentBrowseSite ? 'No subsites or libraries found.' : emptyText}
      </div>
    )
  }

  if (isRootBrowseView) {
    return (
      <>
        {browseSites.map((site) => {
          return (
            <SiteExplorerRow
              key={site.id}
              site={site}
              onOpenSite={onOpenSite}
              onTogglePinnedSite={onTogglePinnedSite}
              pinnedLocationKeys={pinnedLocationKeys}
              selectedDriveId={selectedDriveId}
              selectedSiteId={selectedSiteId}
            />
          )
        })}

        {hasMoreSites ? (
          <LoadMoreButton
            isLoading={isFetchingMoreSites}
            onClick={onLoadMoreSites}
          />
        ) : null}
      </>
    )
  }

  return (
    <>
      {hasSubsitesSection ? (
        <div>
          <SectionLabel
            isExpanded={isSubsitesExpanded}
            onToggle={handleToggleSubsites}
            showTopBorder={false}
          >
            Subsites
          </SectionLabel>
          {isSubsitesExpanded ? (
            <>
              {browseSites.map((site) => {
                return (
                  <SiteExplorerRow
                    key={site.id}
                    site={site}
                    onOpenSite={onOpenSite}
                    onTogglePinnedSite={onTogglePinnedSite}
                    pinnedLocationKeys={pinnedLocationKeys}
                    selectedDriveId={selectedDriveId}
                    selectedSiteId={selectedSiteId}
                  />
                )
              })}

              {hasMoreSites ? (
                <LoadMoreButton
                  isLoading={isFetchingMoreSites}
                  onClick={onLoadMoreSites}
                />
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {hasLibrariesSection ? (
        <div>
          <SectionLabel
            isExpanded={isLibrariesExpanded}
            onToggle={handleToggleLibraries}
            showTopBorder={hasSubsitesSection && isSubsitesExpanded}
          >
            Libraries
          </SectionLabel>
          {isLibrariesExpanded
            ? drives.map((drive) => {
                return (
                  <DriveExplorerRow
                    key={drive.id}
                    drive={drive}
                    onSelectDrive={onSelectDrive}
                    selectedDriveId={selectedDriveId}
                    site={driveBrowseSite}
                  />
                )
              })
            : null}
        </div>
      ) : null}
    </>
  )
})
BrowsePanel.displayName = 'BrowsePanel'

const SavedLocationsPanel = memo(function SavedLocationsPanel({
  emptyText,
  locations,
  onSelectLocation,
  onTogglePinnedLocation,
  selectedDriveId,
  selectedSiteId,
}: SavedLocationsPanelProps) {
  if (locations.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-[12px] text-primary-400 dark:text-primary-500">
        {emptyText}
      </div>
    )
  }

  return locations.map((location) => (
    <SavedLocationRow
      key={location.key}
      location={location}
      onSelectLocation={onSelectLocation}
      onTogglePinnedLocation={onTogglePinnedLocation}
      selectedDriveId={selectedDriveId}
      selectedSiteId={selectedSiteId}
    />
  ))
})
SavedLocationsPanel.displayName = 'SavedLocationsPanel'

const OpenLinkPanel = memo(function OpenLinkPanel({
  isResolvingLink,
  linkError,
  linkValue,
  onLinkValueChange,
  onResolveLink,
}: OpenLinkPanelProps) {
  const handleLinkValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onLinkValueChange(event.target.value)
    },
    [onLinkValueChange],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        onResolveLink()
      }
    },
    [onResolveLink],
  )

  return (
    <div className="space-y-3 px-3 py-3">
      <div className="space-y-1">
        <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
          Open a SharePoint link
        </div>
        <div className="text-xs text-primary-500 dark:text-primary-400">
          Paste a SharePoint site or library URL to jump there directly.
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <LuLink2
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 dark:text-primary-400"
          />
          <Input
            value={linkValue}
            onChange={handleLinkValueChange}
            onKeyDown={handleKeyDown}
            placeholder="https://...sharepoint.com/sites/..."
            className="pl-9"
            autoComplete="off"
          />
        </div>
        <Button
          type="button"
          variant="soft-compact"
          className="h-9 px-3 text-sm rounded-md"
          isLoading={isResolvingLink}
          onClick={onResolveLink}
        >
          Open
        </Button>
      </div>

      {linkError ? (
        <div className="text-xs text-red-600 dark:text-red-400">
          {linkError}
        </div>
      ) : null}
    </div>
  )
})
OpenLinkPanel.displayName = 'OpenLinkPanel'

interface UseBrowserExplorerDataArgs {
  activeTab: ExplorerTab
  currentBrowseSite: SharePointSite | null
  orgId: UUID | ''
}

const useBrowseExplorerData = ({
  activeTab,
  currentBrowseSite,
  orgId,
}: UseBrowserExplorerDataArgs) => {
  const {
    data: rootSites = EMPTY_SITES,
    fetchNextPage: fetchNextRootSitesPage,
    hasNextPage: hasMoreRootSites,
    isFetchingNextPage: isFetchingMoreRootSites,
    isLoading: isRootSitesLoading,
  } = useInfiniteSharepointSites({
    enabled: activeTab === 'browse',
    orgId,
    rootsOnly: true,
    select: selectFlattenedSites,
  })

  const {
    data: subsites = EMPTY_SITES,
    fetchNextPage: fetchNextSubsitesPage,
    hasNextPage: hasMoreSubsites,
    isFetchingNextPage: isFetchingMoreSubsites,
    isLoading: isSubsitesLoading,
  } = useInfiniteSharepointSubsites({
    enabled: activeTab === 'browse' && currentBrowseSite != null,
    orgId,
    siteId: currentBrowseSite?.id ?? '',
    select: selectFlattenedSites,
  })

  const { data: drives = EMPTY_DRIVES, isLoading: isDrivesLoading } =
    useSharepointDrives({
      orgId,
      siteId: currentBrowseSite?.id ?? '',
      enabled: activeTab === 'browse' && currentBrowseSite != null,
      select: selectSortedDrives,
    })

  const browseSites = currentBrowseSite ? subsites : rootSites
  const hasMoreSites = currentBrowseSite ? hasMoreSubsites : hasMoreRootSites
  const isFetchingMoreSites = currentBrowseSite
    ? isFetchingMoreSubsites
    : isFetchingMoreRootSites
  const isBrowseLoading = currentBrowseSite
    ? isSubsitesLoading || isDrivesLoading
    : isRootSitesLoading

  const fetchNextSitesPage = currentBrowseSite
    ? fetchNextSubsitesPage
    : fetchNextRootSitesPage

  return {
    browseSites,
    drives,
    fetchNextSitesPage,
    hasMoreSites,
    isBrowseLoading,
    isFetchingMoreSites,
    rootSites,
  }
}

export const SharepointSiteDriveTree = memo(function SharepointSiteDriveTree({
  className,
  defaultBrowsePath,
  defaultBrowseSite = null,
  emptyText = 'No sites available.',
  onClose,
  onCommitBrowsePath,
  onNavigateToSite,
  onSelectDrive,
  orgId,
  scrollClassName,
  selectedDriveId,
  selectedSiteId,
}: SharepointSiteDriveTreeProps) {
  const recentLocations = useSharepointRecentLocations(orgId)
  const pinnedLocations = useSharepointPinnedLocations(orgId)
  const recordDriveVisit = useSharepointRecordDriveVisit()
  const recordSiteVisit = useSharepointRecordSiteVisit()
  const togglePinnedLocation = useSharepointTogglePinnedLocation()
  const { isPending: isResolvingLink, mutateAsync: resolveSharepointLink } =
    useResolveSharepointLink({ orgId })

  const [activeTab, setActiveTab] = useState<ExplorerTab>('browse')
  const initialBrowsePathKey =
    defaultBrowsePath && defaultBrowsePath.length > 0
      ? getSharepointSitePathKey(defaultBrowsePath)
      : defaultBrowseSite
        ? getSharepointSiteIdentity(defaultBrowseSite)
        : ''
  const initialBrowsePath = useMemo(
    () =>
      getDefaultBrowsePath({
        defaultBrowsePath,
        defaultBrowseSite,
      }),
    [initialBrowsePathKey],
  )
  const [sitePath, setSitePath] = useState<SharePointSite[]>(
    () => initialBrowsePath,
  )
  const initialBrowsePathKeyRef = useRef(initialBrowsePathKey)
  const [linkValue, setLinkValue] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)

  useEffect(() => {
    if (initialBrowsePathKeyRef.current === initialBrowsePathKey) {
      return
    }

    initialBrowsePathKeyRef.current = initialBrowsePathKey
    setSitePath(initialBrowsePath)
  }, [initialBrowsePath, initialBrowsePathKey])

  const currentBrowseSite = sitePath[sitePath.length - 1] ?? null
  const {
    browseSites,
    drives,
    fetchNextSitesPage,
    hasMoreSites,
    isBrowseLoading,
    isFetchingMoreSites,
    rootSites,
  } = useBrowseExplorerData({
    activeTab,
    currentBrowseSite,
    orgId,
  })

  const resolvedSitePath = useMemo(
    () => resolveBrowsePath({ rootSites, sitePath }),
    [rootSites, sitePath],
  )
  const sitePathRef = useRef(resolvedSitePath)
  sitePathRef.current = resolvedSitePath

  const pinnedLocationKeys = useMemo(
    () => new Set(pinnedLocations.map((location) => location.key)),
    [pinnedLocations],
  )
  const isCurrentBrowseSitePinned = useMemo(() => {
    if (!currentBrowseSite) {
      return false
    }

    return pinnedLocationKeys.has(createSiteLocation(currentBrowseSite).key)
  }, [currentBrowseSite, pinnedLocationKeys])

  const handleSelectTab = useCallback((value: string) => {
    setActiveTab(value as ExplorerTab)
  }, [])

  const handleSelectBrowseRoot = useCallback(() => {
    setSitePath([])
    onNavigateToSite?.(null)
  }, [onNavigateToSite])

  const handleSelectBrowseIndex = useCallback(
    (index: number) => {
      if (index < 0) {
        handleSelectBrowseRoot()
        return
      }

      const nextPath = sitePathRef.current.slice(0, index + 1)
      const nextSite = nextPath[index] ?? null
      setSitePath(nextPath)
      onNavigateToSite?.(nextSite)
    },
    [handleSelectBrowseRoot, onNavigateToSite],
  )

  const handleOpenSite = useCallback(
    (site: SharePointSite) => {
      setActiveTab('browse')
      setSitePath([...sitePathRef.current, site])
      onNavigateToSite?.(site)
    },
    [onNavigateToSite],
  )

  const handleJumpToSite = useCallback(
    (site: SharePointSite) => {
      setActiveTab('browse')
      setSitePath([site])
      onNavigateToSite?.(site)
    },
    [onNavigateToSite],
  )

  const handleSelectDrive = useCallback(
    (site: SharePointSite, drive: SharePointDrive) => {
      const currentBrowsePath = sitePathRef.current
      const committedBrowsePath =
        currentBrowsePath[currentBrowsePath.length - 1]?.id === site.id
          ? currentBrowsePath
          : [site]

      onCommitBrowsePath?.(committedBrowsePath)
      recordDriveVisit(orgId, site, drive)
      onNavigateToSite?.(site)
      onSelectDrive(site, drive)
      onClose?.()
    },
    [
      orgId,
      onClose,
      onCommitBrowsePath,
      onNavigateToSite,
      onSelectDrive,
      recordDriveVisit,
    ],
  )

  const handleTogglePinnedSite = useCallback(
    (site: SharePointSite) => {
      togglePinnedLocation(orgId, createSiteLocation(site))
    },
    [orgId, togglePinnedLocation],
  )
  const handleTogglePinnedCurrentBrowseSite = useCallback(() => {
    if (!currentBrowseSite) {
      return
    }

    handleTogglePinnedSite(currentBrowseSite)
  }, [currentBrowseSite, handleTogglePinnedSite])

  const handleTogglePinnedSavedLocation = useCallback(
    (location: SharepointSavedLocation) => {
      togglePinnedLocation(orgId, location)
    },
    [orgId, togglePinnedLocation],
  )

  const handleSelectSavedLocation = useCallback(
    (location: SharepointSavedLocation) => {
      const site = createSiteFromSavedLocation(location)
      const drive = createDriveFromSavedLocation(location)

      if (drive) {
        handleSelectDrive(site, drive)
        return
      }

      recordSiteVisit(orgId, site)
      handleJumpToSite(site)
    },
    [handleJumpToSite, handleSelectDrive, orgId, recordSiteVisit],
  )

  const handleResolveLink = useCallback(async () => {
    const normalizedTarget = normalizeSharepointUrl(linkValue)

    if (!normalizedTarget) {
      setLinkError('Enter a valid SharePoint site or library URL.')
      return
    }

    setLinkError(null)

    try {
      const resolvedLocation = await resolveSharepointLink({
        url: linkValue,
      })

      setLinkValue('')
      setActiveTab('browse')

      if (resolvedLocation.drive) {
        handleSelectDrive(resolvedLocation.site, resolvedLocation.drive)
        return
      }

      recordSiteVisit(orgId, resolvedLocation.site)
      handleJumpToSite(resolvedLocation.site)
    } catch (error) {
      console.error('Failed to resolve SharePoint link:', error)
      setLinkError(getResolveLinkErrorMessage(error))
    }
  }, [
    linkValue,
    orgId,
    handleJumpToSite,
    handleSelectDrive,
    recordSiteVisit,
    resolveSharepointLink,
  ])

  const handleLinkValueChange = useCallback((value: string) => {
    setLinkValue(value)
  }, [])

  const handleResolveLinkClick = useCallback(() => {
    void handleResolveLink()
  }, [handleResolveLink])

  const handleLoadMoreSites = useCallback(() => {
    void fetchNextSitesPage()
  }, [fetchNextSitesPage])

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleSelectTab}
      className={twMerge(
        'flex min-h-0 flex-col overflow-hidden divide-y divide-primary-200 dark:divide-primary-800',
        className,
      )}
    >
      <div className="shrink-0 px-1.5 py-1.5">
        <TabsList variant="grouped" className="h-7">
          {EXPLORER_TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              variant="grouped"
              className="min-w-0 px-1 text-[11px] sm:px-2 sm:text-[12px]"
            >
              <span className="truncate sm:hidden">{tab.compactLabel}</span>
              <span className="hidden truncate sm:inline">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {activeTab === 'browse' ? (
        <BrowseBreadcrumbs
          isCurrentSitePinned={isCurrentBrowseSitePinned}
          onTogglePinnedCurrentSite={
            currentBrowseSite ? handleTogglePinnedCurrentBrowseSite : undefined
          }
          path={resolvedSitePath}
          onSelectIndex={handleSelectBrowseIndex}
        />
      ) : null}

      <div
        className={twMerge('flex-1 overflow-y-auto py-1.5', scrollClassName)}
      >
        <TabsContent value="browse" className="mt-0">
          <BrowsePanel
            browseSites={browseSites}
            currentBrowseSite={currentBrowseSite}
            driveBrowseSite={currentBrowseSite}
            drives={drives}
            emptyText={emptyText}
            hasMoreSites={hasMoreSites}
            isBrowseLoading={isBrowseLoading}
            isFetchingMoreSites={isFetchingMoreSites}
            onLoadMoreSites={handleLoadMoreSites}
            onOpenSite={handleOpenSite}
            onSelectDrive={handleSelectDrive}
            onTogglePinnedSite={handleTogglePinnedSite}
            pinnedLocationKeys={pinnedLocationKeys}
            selectedDriveId={selectedDriveId}
            selectedSiteId={selectedSiteId}
          />
        </TabsContent>

        <TabsContent value="recent" className="mt-0">
          <SavedLocationsPanel
            emptyText="No recent SharePoint locations yet."
            locations={recentLocations}
            onSelectLocation={handleSelectSavedLocation}
            onTogglePinnedLocation={handleTogglePinnedSavedLocation}
            selectedDriveId={selectedDriveId}
            selectedSiteId={selectedSiteId}
          />
        </TabsContent>

        <TabsContent value="pinned" className="mt-0">
          <SavedLocationsPanel
            emptyText="Pin a site to keep it handy."
            locations={pinnedLocations}
            onSelectLocation={handleSelectSavedLocation}
            onTogglePinnedLocation={handleTogglePinnedSavedLocation}
            selectedDriveId={selectedDriveId}
            selectedSiteId={selectedSiteId}
          />
        </TabsContent>

        <TabsContent value="open-link" className="mt-0">
          <OpenLinkPanel
            isResolvingLink={isResolvingLink}
            linkError={linkError}
            linkValue={linkValue}
            onLinkValueChange={handleLinkValueChange}
            onResolveLink={handleResolveLinkClick}
          />
        </TabsContent>
      </div>
    </Tabs>
  )
})
SharepointSiteDriveTree.displayName = 'SharepointSiteDriveTree'
